import {
  Banner,
  FormField,
  FormGrid,
  SelectionList,
  Tabs,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { client as elevioClient } from 'elevio';
import type { HttpMethod } from 'lib-shared-types-api';
import { useState } from 'react';
import {
  Controller,
  ControllerProps,
  FieldValues,
  Path,
  useFormContext,
  UseFormGetValues,
  UseFormReset,
  useFormState,
  useWatch,
} from 'react-hook-form';

import { FieldTemplate } from 'components/fieldTemplate';
import { HookFormSelectionList, HookFormText } from 'components/hookForm';
import { templateHelpText } from 'components/settings/components/sections/cards/cards.utils';
import { articles } from 'utils/elevio';
import { validateName } from 'utils/formValidator';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useNavigatePrompt } from 'utils/url/useNavigatePrompt';

import { editorMaxLines } from '../../../../configuration';
import type { AuthorizationType } from '../../../components/authorizationHeader/authorizationHeader.types';
import { OAuthFormData } from '../../../components/oAuth';
import {
  validateNotOnlyWhitespace,
  validateUrlUsesHttps,
} from '../../../utils/validateMessages';

import { RequestBody } from './components/requestBody/requestBody';
import { RequestHeaders } from './components/requestHeaders/requestHeaders';
import styles from './settingsIntegrationsCardsCustomForm.module.scss';

type Tab = {
  id: string;
  label: string;
  disabled: boolean;
  slotEnd?: JSX.Element | false;
};

const httpMethods: readonly HttpMethod[] = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
];

type CustomCardsFormProps<TFieldValues extends FieldValues> = {
  control: ControllerProps<TFieldValues>['control'];
  handleCustomCardSubmit: () => void;
  reset: UseFormReset<TFieldValues>;
  getValues: UseFormGetValues<TFieldValues>;
};

const defaultAuthorizationType: AuthorizationType = 'none';

export const CustomCardsForm = <TFieldValues extends FieldValues>({
  control,
  handleCustomCardSubmit,
  reset,
  getValues,
}: CustomCardsFormProps<TFieldValues>): JSX.Element => {
  const [selectedTab, setSelectedTab] = useState('headers');
  const [selectedAuthorizationType, setSelectedAuthorizationType] =
    useState<AuthorizationType>(defaultAuthorizationType);

  const isMediumScreen = useBreakpointValue({ base: true, lg: false });
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const {
    formState: {
      dirtyFields: oAuthFormDirtyFields,
      isSubmitting: isOAuthFormSubmitting,
    },
  } = useFormContext<OAuthFormData>();

  const {
    dirtyFields: customCardFormDirtyFields,
    isSubmitting: isCustomCardFormSubmitting,
    errors: customCardErrors,
  } = useFormState<TFieldValues>({ control });

  const isAnyFormDirty =
    Object.keys({
      ...customCardFormDirtyFields,
      ...oAuthFormDirtyFields,
    }).length > 0;

  useNavigatePrompt(
    isAnyFormDirty && !isCustomCardFormSubmitting && !isOAuthFormSubmitting,
  );

  const [
    requestUriTemplateFormValue,
    requestBodyFormValue,
    requestContentTypeFormValue,
    authorizationHeaderFormValue,
    customHeadersFormValue,
    requestHttpMethodFormValue,
  ] = useWatch({
    control,
    name: [
      'request.uri_template',
      'request.body',
      'request.content_type',
      'authorizationHeader',
      'customHeaders',
      'request.http_method',
    ] as Path<TFieldValues>[],
  });

  const isGetHttpMethod =
    requestHttpMethodFormValue?.length > 0 &&
    requestHttpMethodFormValue[0] === 'GET';

  const hasUriTemplate =
    !!requestUriTemplateFormValue && requestUriTemplateFormValue !== '';

  const hasRequestFormData =
    selectedAuthorizationType !== 'none' ||
    (!!authorizationHeaderFormValue && authorizationHeaderFormValue !== '') ||
    !!customHeadersFormValue ||
    !!requestBodyFormValue ||
    requestContentTypeFormValue?.length > 0;

  const hasRequestHeadersFormError =
    'authorizationHeader' in customCardErrors &&
    selectedAuthorizationType !== 'none';

  const formErrorIndicator = (
    <Tooltip content="You have form errors in this tab">
      <span className={styles.errorIndicator}></span>
    </Tooltip>
  );

  const tabs: Tab[] = [
    {
      id: 'headers',
      label: 'Header',
      disabled: !hasUriTemplate,
      slotEnd: hasRequestHeadersFormError ? formErrorIndicator : false,
    },
    {
      id: 'body',
      label: 'Body',
      disabled: !hasUriTemplate || isGetHttpMethod,
    },
  ];

  return (
    <form>
      <FormGrid>
        <FormField span={isMediumScreen ? 6 : 3}>
          <HookFormText
            control={control}
            label="Title"
            name={'title' as Path<TFieldValues>}
            rules={{
              required: {
                value: true,
                message: 'Please add a title',
              },
              validate: validateName,
            }}
            componentProps={{
              placeholder: 'Insert title',
            }}
          />
        </FormField>
        {!isMediumScreen && <FormField span={3} />}

        <FormField span={isSmallScreen ? 6 : 1}>
          <HookFormSelectionList
            control={control}
            label="Method"
            name={'request.http_method' as Path<TFieldValues>}
            rules={{
              required: {
                value: true,
                message: 'Please select a method',
              },
            }}
            componentProps={{
              onChange: (event) => {
                if (event?.selectedValues[0] === 'GET') {
                  const allValues = getValues();
                  // We reset the fields `request.content_type` and
                  // `request.body` when the selected `http_method` is `GET`,
                  // because a GET request is not allowed to have a body.
                  reset({
                    ...allValues,
                    request: {
                      ...allValues.request,
                      body: '',
                      content_type: [],
                      http_method: event?.selectedValues,
                    },
                  });
                }
              },
              placeholder: 'Select method',
              getItemTitleByValue: (method: HttpMethod | null | undefined) => (
                <Text.Paragraph>{method}</Text.Paragraph>
              ),
            }}
          >
            {httpMethods.map((method) => (
              <SelectionList.Item key={method} value={method}>
                {method}
              </SelectionList.Item>
            ))}
          </HookFormSelectionList>
        </FormField>

        <FormField span={isSmallScreen ? 6 : 5}>
          <HookFormText
            control={control}
            label="Hook URL"
            name={'request.uri_template' as Path<TFieldValues>}
            rules={{
              validate: (hookUrl) => validateUrlUsesHttps(hookUrl),
            }}
            helpIcon={{
              title: 'Hook URL help',
              text: (
                <>
                  See the{' '}
                  <span
                    onClick={() =>
                      elevioClient.openArticle(articles.settings.cards.custom)
                    }
                  >
                    Custom card integrations documentation
                  </span>{' '}
                  for all details on how to build a hook URL with variables.
                </>
              ),
              requireClick: true,
              tooltipClassName: styles.wideTooltip,
            }}
            componentProps={{
              placeholder: 'Insert hook URL',
            }}
          />
        </FormField>

        {!hasUriTemplate && (
          <FormField>
            <Banner
              variant={hasRequestFormData ? 'warning' : 'info'}
              headline="Request headers and body"
              description={
                hasRequestFormData ? (
                  <>
                    You removed the hook URL. Doing so will also drop the
                    authorization header, all custom headers, the body and
                    content-type from your request.
                  </>
                ) : (
                  <>
                    Add a hook URL to let the card make a HTTP request. You will
                    then have the option to add an authorization header, custom
                    headers and/or a body and content-type to your request.
                  </>
                )
              }
            />
          </FormField>
        )}

        <FormField>
          <Tabs
            selectedId={selectedTab}
            tabs={tabs}
            onChange={(tabId) => setSelectedTab(tabId)}
            className={styles.tabs}
          />

          <RequestHeaders
            disabled={!hasUriTemplate}
            control={control}
            className={classnames(styles.tabsChild, {
              [styles.hidden]: selectedTab !== 'headers',
            })}
            selectedAuthorizationType={selectedAuthorizationType}
            setSelectedAuthorizationType={setSelectedAuthorizationType}
            handleCustomCardSubmit={handleCustomCardSubmit}
          />

          <RequestBody
            disabled={!hasUriTemplate || isGetHttpMethod}
            control={control}
            className={classnames(styles.tabsChild, {
              [styles.hidden]: selectedTab !== 'body',
            })}
          />
        </FormField>

        <FormField>
          <Controller
            name={'template' as Path<TFieldValues>}
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Please add some template content',
              },
              validate: (template) => validateNotOnlyWhitespace(template),
            }}
            render={({ field, fieldState }) => (
              <FieldTemplate
                required
                label="Template content"
                input={field}
                placeholder="Insert template"
                height="100%"
                editorOptions={{ maxLines: editorMaxLines }}
                helpIcon={{
                  title: 'Template help',
                  text: templateHelpText,
                  requireClick: true,
                }}
                meta={{
                  error: fieldState?.error?.message,
                }}
              />
            )}
          />
        </FormField>
      </FormGrid>
    </form>
  );
};
